import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { CompatReportDocument } from '../types';
import {
  bandLabel,
  formatPct,
  resolvePerPipeDisplayRows,
  sortPerPipeDisplayRows,
  type PerPipeSortKey,
} from '../helpers/formatReport';
import { Input } from '../../../../../components/ui/input';
import { cn } from '../../../../../lib/utils';

interface PerPipeViewProps {
  result: CompatReportDocument;
}

const bandClass: Record<string, string> = {
  safe: 'text-galvanizing-green bg-[rgba(31,154,58,0.10)]',
  atRisk: 'text-[#B38600] bg-[rgba(230,161,0,0.12)]',
  rework: 'text-readable-red bg-[rgba(196,0,31,0.08)]',
};

export const PerPipeView: React.FC<PerPipeViewProps> = ({ result }) => {
  const perPipe = result.weldability.perPipe;
  const deadEnds = result.weldability.deadEnds ?? null;
  const [sortKey, setSortKey] = useState<PerPipeSortKey>('goesInRate');
  const [dir, setDir] = useState<1 | -1>(1);
  const [search, setSearch] = useState('');

  const displaySource = useMemo(
    () => resolvePerPipeDisplayRows(result.weldability),
    [result.weldability]
  );

  const poolRows = useMemo(() => displaySource.filter((p) => !p.isDeadEnd), [displaySource]);
  const deadRows = useMemo(() => displaySource.filter((p) => p.isDeadEnd), [displaySource]);

  const bandCounts = useMemo(() => {
    return poolRows.reduce(
      (acc, p) => {
        const key = String(p.band || 'atRisk');
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [poolRows]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = displaySource.filter((p) => {
      if (!q) return true;
      return (
        p.pipeId.toLowerCase().includes(q) ||
        String(p.band).toLowerCase().includes(q) ||
        bandLabel(p.band).toLowerCase().includes(q) ||
        (p.isDeadEnd && ('dead end'.includes(q) || 'ends line'.includes(q) || 'set aside'.includes(q)))
      );
    });
    return sortPerPipeDisplayRows(filtered, sortKey, dir);
  }, [displaySource, sortKey, dir, search]);

  const deadNote = useMemo(() => {
    if (!deadEnds?.pipes?.length) return null;
    const asScanned = result.weldability.perPipe?.pipes || [];
    return deadEnds.pipes
      .map((p) => {
        const scanned = asScanned.find((r) => r.pipeId === p.pipeId);
        const r = scanned?.goesInRate ?? 0;
        const good = p.deadEnd === 'B' ? 'A' : 'B';
        return `* Pipe ${p.pipeId}: ${formatPct(r)} means it joined the line in ${
          r === 1 ? 'every build' : 'that share of builds'
        }, always through end ${good}.`;
      })
      .join(' ');
  }, [deadEnds, result.weldability.perPipe?.pipes]);

  const onSort = (key: PerPipeSortKey) => {
    if (key === sortKey) setDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setDir(1);
    }
  };

  if (!perPipe) {
    return (
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 text-[14px] text-heritage-grey">
        Per-pipe results are not available yet.
      </div>
    );
  }

  const safe = bandCounts.safe ?? perPipe.safe ?? 0;
  const atRisk = bandCounts.atRisk ?? perPipe.atRisk ?? 0;
  const rework = bandCounts.rework ?? perPipe.rework ?? 0;
  const setAside = deadRows.length;
  const usingUsablePool = Boolean(deadEnds?.withoutThem?.perPipe?.length);

  const stats = [
    { v: poolRows.length + setAside, k: 'Total pipes', accent: 'bg-hero-red' },
    { v: safe, k: 'Safe', accent: 'bg-galvanizing-green' },
    { v: atRisk, k: 'At risk', accent: 'bg-[#E6A100]' },
    { v: rework, k: 'Rework', accent: 'bg-readable-red' },
    ...(setAside > 0
      ? [{ v: setAside, k: 'Set aside', accent: 'bg-readable-red' }]
      : []),
  ];

  return (
    <div className="space-y-4 max-w-full mx-auto w-full">
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-1">
          Every pipe and how often it welds in
          {usingUsablePool ? ' — usable-pool rates, set-aside pipes last' : ''}
        </p>
        <p className="text-[15px] text-text-grey mb-4">
          <span className="font-semibold text-blueprint-blue">{safe} safe</span>
          {', '}
          <span className="font-semibold text-blueprint-blue">{atRisk} at risk</span>
          {', '}
          <span className="font-semibold text-blueprint-blue">{rework} rework</span>
          {setAside > 0 ? (
            <>
              {', '}
              <span className="font-semibold text-readable-red">{setAside} set aside</span>
            </>
          ) : null}
          .
        </p>
        <div
          className={cn(
            'grid gap-3',
            setAside > 0 ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'
          )}
        >
          {stats.map((s) => (
            <div
              key={s.k}
              className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center relative overflow-hidden"
            >
              <div className={cn('absolute top-0 left-0 right-0 h-[2px]', s.accent)} />
              <div className="font-heading font-bold text-[22px] text-blueprint-blue">{s.v}</div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                {s.k}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-4 sm:p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey">
            Pipe list
            {search.trim() ? (
              <span className="normal-case tracking-normal ml-2 text-text-grey">
                · {rows.length} match{rows.length === 1 ? '' : 'es'}
              </span>
            ) : null}
          </p>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-heritage-grey" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pipe ID or verdict…"
              className="pl-9 h-9"
              aria-label="Search pipes"
            />
          </div>
        </div>

        <div className="overflow-auto border border-[#E2E5EA] rounded-[10px] max-h-[min(70vh,640px)]">
          <table className="w-full table-fixed border-collapse text-[13.5px]">
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[30%]" />
              <col className="w-[30%]" />
              <col className="w-[30%]" />
            </colgroup>
            <thead>
              <tr>
                <th className="sticky top-0 z-[1] bg-page-bg px-3 py-2.5 text-center text-[10px] font-bold tracking-wide uppercase text-heritage-grey border-b border-[#E2E5EA]">
                  S.No.
                </th>
                {(
                  [
                    { key: 'pipeId' as const, label: 'Pipe' },
                    { key: 'band' as const, label: 'Verdict' },
                    { key: 'goesInRate' as const, label: 'Goes in' },
                  ] as const
                ).map((col) => (
                  <th
                    key={col.key}
                    onClick={() => onSort(col.key)}
                    className="sticky top-0 z-[1] bg-page-bg px-3 py-2.5 text-center text-[10px] font-bold tracking-wide uppercase text-heritage-grey cursor-pointer select-none border-b border-[#E2E5EA]"
                  >
                    {col.label}
                    {sortKey === col.key ? (dir === 1 ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-[13px] text-heritage-grey">
                    No pipes match “{search.trim()}”.
                  </td>
                </tr>
              ) : (
                rows.map((p, index) => (
                  <tr
                    key={p.pipeId}
                    className={cn(
                      'border-b border-[#EEF0F3] last:border-b-0',
                      p.isDeadEnd && 'bg-[rgba(196,0,31,0.04)]'
                    )}
                  >
                    <td className="px-3 py-2 text-center tabular-nums text-heritage-grey">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-blueprint-blue">{p.pipeId}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.isDeadEnd ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11.5px] font-semibold text-readable-red bg-[rgba(196,0,31,0.08)]">
                          Ends line
                        </span>
                      ) : (
                        <span
                          className={cn(
                            'inline-block px-2 py-0.5 rounded-full text-[11.5px] font-semibold',
                            bandClass[p.band] || bandClass.atRisk
                          )}
                        >
                          {bandLabel(p.band)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums">
                      {p.isDeadEnd ? (
                        <span className="text-readable-red font-semibold">
                          {formatPct(p.goesInRate)}*
                        </span>
                      ) : (
                        formatPct(p.goesInRate)
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {deadNote ? (
          <p className="mt-4 text-[13.5px] text-readable-red">
            <span className="font-semibold">{deadNote}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};
